/**
 * Sample report fixture
 * ---
 * A fully-populated `PremiumReportSections` object used for the public
 * `/demo-report` preview route and any internal storybook-style screens.
 *
 * The fictional creator is `@ember_kalani` — a beauty / lifestyle
 * Instagram creator with 412K followers. Every numeric value sits in a
 * realistic, internally-consistent range so the report charts render
 * without weird artefacts. Anyone can hit `/demo-report` and see what
 * the full premium report looks like end-to-end.
 */

import type {
  PremiumReportSections,
  ReportPayload,
  Entitlement,
} from '../lib/reportsApi';

const audienceRow = (label: string, pct: number) => ({
  label,
  followers_pct: pct,
  followers_count: Math.round(412_000 * pct),
  likers_pct: pct,
  likers_count: Math.round(45_000 * pct),
});

export const DEMO_REPORT_SECTIONS: PremiumReportSections = {
  variant: 'premium',

  snapshot: {
    handle: 'ember_kalani',
    platform: 'instagram',
    profile_url: 'https://instagram.com/ember_kalani',
    profile_image_url: null,
    followers: 412_000,
    engagement_rate: 0.048,
    avg_views: 138_000,
    avg_likes: 19_800,
    posts_count: 1_842,
    primary_category: 'Beauty & Cosmetics',
    secondary_categories: ['Fashion', 'Travel'],
    niche_summary:
      'Beauty creator with a strong Gen-Z female audience, polished editorial aesthetic, and high authentic engagement.',
    positioning:
      'Sits in the top 17% of beauty creators in her cohort. Audience over-indexes on premium skincare and travel.',
    is_mock: true,
  },

  audience: {
    gender_split: { male: 0.18, female: 0.82 },
    age_distribution: [
      audienceRow('13-17', 0.06),
      audienceRow('18-24', 0.32),
      audienceRow('25-34', 0.41),
      audienceRow('35-44', 0.14),
      audienceRow('45-54', 0.05),
      audienceRow('55+',   0.02),
    ],
    age_distribution_male: [
      audienceRow('13-17', 0.012),
      audienceRow('18-24', 0.062),
      audienceRow('25-34', 0.072),
      audienceRow('35-44', 0.022),
      audienceRow('45-54', 0.008),
      audienceRow('55+',   0.004),
    ],
    age_distribution_female: [
      audienceRow('13-17', 0.048),
      audienceRow('18-24', 0.258),
      audienceRow('25-34', 0.338),
      audienceRow('35-44', 0.118),
      audienceRow('45-54', 0.042),
      audienceRow('55+',   0.016),
    ],
    top_countries: [
      audienceRow('United States',   0.41),
      audienceRow('United Kingdom',  0.11),
      audienceRow('Canada',          0.08),
      audienceRow('Australia',       0.07),
      audienceRow('Germany',         0.05),
      audienceRow('France',          0.04),
      audienceRow('Brazil',          0.03),
      audienceRow('Mexico',          0.03),
    ],
    top_cities: [
      { name: 'Los Angeles',     weight: 0.092 },
      { name: 'New York',        weight: 0.071 },
      { name: 'London',          weight: 0.046 },
      { name: 'Toronto',         weight: 0.032 },
      { name: 'Sydney',          weight: 0.028 },
      { name: 'Chicago',         weight: 0.024 },
    ],
    audience_languages: [
      { name: 'English',    weight: 0.78 },
      { name: 'Spanish',    weight: 0.09 },
      { name: 'French',     weight: 0.05 },
      { name: 'Portuguese', weight: 0.04 },
      { name: 'German',     weight: 0.02 },
    ],
    audience_interests: [
      audienceRow('Beauty & Cosmetics', 0.42),
      audienceRow('Fashion',            0.31),
      audienceRow('Travel',             0.22),
      audienceRow('Wellness',           0.17),
      audienceRow('Photography',        0.12),
      audienceRow('Food & Drink',       0.10),
      audienceRow('Fitness',            0.08),
      audienceRow('Music',              0.07),
    ],
    brand_affinity: [
      audienceRow('Sephora',     0.094),
      audienceRow('Glossier',    0.082),
      audienceRow('Charlotte Tilbury', 0.071),
      audienceRow('Dior',        0.068),
      audienceRow('Rare Beauty', 0.061),
      audienceRow('Fenty Beauty', 0.054),
      audienceRow('Drunk Elephant', 0.047),
      audienceRow('La Mer',      0.041),
    ],
    credibility_score: 0.86,
    paid_post_performance: 0.91,
    reachability_label: 'high',
    reachability_score: 0.78,
  },

  cohort: {
    cohort_label: 'Beauty creators · 250K-500K followers · Instagram',
    follower_tier: '250K-500K',
    income_low:    3_400,
    income_avg:    8_200,
    income_high:  18_500,
    engagement_p25: 0.018,
    engagement_p50: 0.029,
    engagement_p75: 0.043,
    engagement_p90: 0.061,
    sample_size: 184,
    your_engagement: 0.048,
    your_percentile: 83,
    gap_to_top: { metric: 'engagement_rate', you: 0.048, top: 0.061 },
  },

  monetization_overview: {
    total_estimated_low:      72_000,
    total_estimated_expected: 148_000,
    total_estimated_high:    246_000,
    tier: 2,
    confidence: 0.74,
    mix: [
      { category: 'brand_deals',      label: 'Brand deals',      low: 28_000, expected: 56_000, high: 92_000, locked: false },
      { category: 'affiliate',        label: 'Affiliate',        low: 14_000, expected: 27_000, high: 45_000, locked: false },
      { category: 'product_sales',    label: 'Product sales',    low: 18_000, expected: 38_000, high: 68_000, locked: true  },
      { category: 'digital_products', label: 'Digital products', low:  8_000, expected: 18_000, high: 28_000, locked: true  },
      { category: 'memberships',      label: 'Memberships',      low:  4_000, expected:  9_000, high: 13_000, locked: true  },
    ],
    what_creatorhub_unlocks: [
      'Pre-vetted vendor pipeline matched to your audience',
      'Direct path to launch a private-label SKU in beauty',
      'Posting timing intelligence (peak engagement windows)',
      'Brand-deal value bands for outreach',
      'A prioritized 6-step roadmap with effort × impact tagging',
      'Quarterly performance benchmark vs your cohort',
    ],
  },

  content_signals: {
    posting_cadence: 'Posting 4.2x per week · highest engagement on Wed / Sat evenings.',
    format_breakdown: [
      { name: 'Reels',  value: 48 },
      { name: 'Photos', value: 27 },
      { name: 'Stories',value: 18 },
      { name: 'IGTV',   value: 7  },
    ],
    engagement_trend: [
      { name: 'W1',  engagement: 0.042 },
      { name: 'W2',  engagement: 0.045 },
      { name: 'W3',  engagement: 0.041 },
      { name: 'W4',  engagement: 0.048 },
      { name: 'W5',  engagement: 0.052 },
      { name: 'W6',  engagement: 0.049 },
      { name: 'W7',  engagement: 0.053 },
      { name: 'W8',  engagement: 0.051 },
      { name: 'W9',  engagement: 0.057 },
      { name: 'W10', engagement: 0.055 },
      { name: 'W11', engagement: 0.061 },
      { name: 'W12', engagement: 0.064 },
    ],
    posting_heatmap: (() => {
      const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      const hours = ['6am', '9am', '12pm', '3pm', '6pm', '9pm'];
      const peaks: Record<string, number> = {
        'Wed-6pm': 9, 'Wed-9pm': 8, 'Sat-9pm': 9, 'Sat-6pm': 8,
        'Sun-12pm': 7, 'Mon-6pm': 5, 'Thu-6pm': 7, 'Fri-9pm': 7,
        'Tue-9am': 4, 'Sun-6pm': 6,
      };
      const out: Array<{ row: string; col: string; value: number }> = [];
      for (const r of days) {
        for (const c of hours) {
          out.push({ row: r, col: c, value: peaks[`${r}-${c}`] ?? Math.round(Math.random() * 3) });
        }
      }
      return out;
    })(),
    popular_hashtags: [
      { tag: 'beauty',     pct: 0.12 },
      { tag: 'skincare',   pct: 0.09 },
      { tag: 'glowup',     pct: 0.07 },
      { tag: 'travel',     pct: 0.05 },
      { tag: 'wellness',   pct: 0.04 },
      { tag: 'makeuptutorial', pct: 0.04 },
    ],
    popular_mentions: [
      { mention: 'sephora',         pct: 0.046 },
      { mention: 'glossier',        pct: 0.038 },
      { mention: 'charlottetilbury', pct: 0.029 },
      { mention: 'rarebeauty',      pct: 0.024 },
    ],
  },

  product_matches: [
    {
      rank: 1, product_id: 1001, title: 'Gold-Infused Hydrating Serum (30ml)',
      primary_image_url: null, vertical: 'beauty', category: 'Skincare',
      price_min: 22, price_max: 28, score: 0.92,
      why: 'Strong tag overlap with skincare interests + premium beauty brand affinity. Audience over-indexes on $20-40 SKU price band.',
      gmv_low: 18_000, gmv_expected: 32_000, gmv_high: 54_000,
    },
    {
      rank: 2, product_id: 1002, title: 'Vegan Lash & Brow Boost Set',
      primary_image_url: null, vertical: 'beauty', category: 'Cosmetics',
      price_min: 18, price_max: 24, score: 0.88,
      why: 'Aligns with female-skewed Gen-Z audience. Cohort historically converts at 3.1% on cosmetics SKUs in this price band.',
      gmv_low: 14_000, gmv_expected: 27_000, gmv_high: 42_000,
    },
    {
      rank: 3, product_id: 1003, title: 'Travel-Size Skincare Bundle (4-pack)',
      primary_image_url: null, vertical: 'beauty', category: 'Skincare',
      price_min: 36, price_max: 42, score: 0.84,
      why: 'Combines beauty + travel verticals — both top-2 audience interests. Strong fit for content tie-ins.',
      gmv_low: 11_000, gmv_expected: 22_000, gmv_high: 38_000,
    },
    {
      rank: 4, product_id: 1004, title: 'Silk Hair Wrap & Sleep Mask Set',
      primary_image_url: null, vertical: 'apparel', category: 'Accessories',
      price_min: 28, price_max: 38, score: 0.79,
      why: 'Soft adjacency to wellness vertical (top-4 audience interest). Higher margin than core SKUs.',
      gmv_low: 8_000, gmv_expected: 16_000, gmv_high: 28_000,
    },
    {
      rank: 5, product_id: 1005, title: 'Daily Glow Vitamin C Mist (100ml)',
      primary_image_url: null, vertical: 'beauty', category: 'Skincare',
      price_min: 16, price_max: 22, score: 0.76,
      why: 'Entry-price beauty SKU — fits audience with mixed disposable income. Low complexity to launch.',
      gmv_low: 6_000, gmv_expected: 13_000, gmv_high: 22_000,
    },
  ],

  vendor_matches: [
    {
      vendor_id: 'v_001', name: 'Lumina Naturals',
      category: 'Skincare', fit_score: 0.91,
      commission_band: { low: 18, high: 26 },
      why: 'Clean-beauty house with Asia-EU fulfilment. Has private-label capacity in 4-week MOQ for skincare serums.',
    },
    {
      vendor_id: 'v_002', name: 'Aurora Cosmetics Co.',
      category: 'Cosmetics', fit_score: 0.88,
      commission_band: { low: 22, high: 30 },
      why: 'Pre-vetted by CreatorHub. Specializes in vegan lash, brow, and lip products. Will white-label below 10K MOQ.',
    },
    {
      vendor_id: 'v_003', name: 'Wanderwell Beauty',
      category: 'Skincare', fit_score: 0.83,
      commission_band: { low: 16, high: 22 },
      why: 'Travel-size and bundled SKU specialist. Strong fit given creator\'s travel content overlap.',
    },
    {
      vendor_id: 'v_004', name: 'Silk & Slumber',
      category: 'Accessories', fit_score: 0.74,
      commission_band: { low: 25, high: 35 },
      why: 'Wellness accessories — silk and bamboo. Higher margin band; lower volume.',
    },
    {
      vendor_id: 'v_005', name: 'Glow Lab Supply',
      category: 'Skincare', fit_score: 0.71,
      commission_band: { low: 15, high: 20 },
      why: 'Entry-tier skincare house. Lower price band, high volume potential.',
    },
    {
      vendor_id: 'v_006', name: 'Ember Apothecary',
      category: 'Wellness', fit_score: 0.68,
      commission_band: { low: 20, high: 28 },
      why: 'Botanical wellness line. Soft adjacency to top audience interests.',
    },
  ],

  brand_deal_opportunities: [
    {
      brand_name: 'Glossier',
      category: 'Beauty',
      fit_score: 0.93,
      estimated_value_low: 18_000,
      estimated_value_high: 32_000,
      why: 'Top-2 audience brand affinity. Matches creator\'s editorial aesthetic and ER profile.',
    },
    {
      brand_name: 'Sephora',
      category: 'Beauty',
      fit_score: 0.91,
      estimated_value_low: 22_000,
      estimated_value_high: 38_000,
      why: 'Top-1 brand affinity. Frequently runs creator collabs in this follower band.',
    },
    {
      brand_name: 'Drunk Elephant',
      category: 'Skincare',
      fit_score: 0.86,
      estimated_value_low: 12_000,
      estimated_value_high: 22_000,
      why: 'Strong audience overlap on skincare interest. Brand has active creator program.',
    },
    {
      brand_name: 'Rare Beauty',
      category: 'Cosmetics',
      fit_score: 0.84,
      estimated_value_low: 14_000,
      estimated_value_high: 24_000,
      why: 'Top-5 audience brand affinity, strong Gen-Z alignment.',
    },
    {
      brand_name: 'Charlotte Tilbury',
      category: 'Beauty',
      fit_score: 0.82,
      estimated_value_low: 16_000,
      estimated_value_high: 28_000,
      why: 'Premium beauty house, audience fits the brand\'s aspirational profile.',
    },
    {
      brand_name: 'Aman Hotels',
      category: 'Travel',
      fit_score: 0.71,
      estimated_value_low: 8_000,
      estimated_value_high: 18_000,
      why: 'Travel adjacency. Strong fit for sponsored editorial trips.',
    },
  ],

  monetization_full: {
    by_category_breakdown: [
      { category: 'Brand deals',      monthly_low: 2_300, monthly_expected: 4_600, monthly_high: 7_700, drivers: ['ER above cohort p75', 'High brand affinity'] },
      { category: 'Affiliate',        monthly_low: 1_100, monthly_expected: 2_200, monthly_high: 3_700, drivers: ['Beauty + travel CTR', 'Higher AOV in audience cohort'] },
      { category: 'Product sales',    monthly_low: 1_500, monthly_expected: 3_100, monthly_high: 5_600, drivers: ['Audience price-band fit', 'Skincare conversion lift'] },
      { category: 'Digital products', monthly_low:   650, monthly_expected: 1_500, monthly_high: 2_300, drivers: ['Tutorial format mix', 'Wellness adjacency'] },
      { category: 'Memberships',      monthly_low:   330, monthly_expected:   750, monthly_high: 1_080, drivers: ['Authenticity score', 'Reachability label'] },
    ],
  },

  action_plan_basic: [
    { rank: 1, title: 'Pilot a private-label skincare SKU',
      description: 'Use Lumina Naturals\' 4-week MOQ to test a hydrating serum at the $24-28 price point. Highest-impact action given audience price band + interest overlap.',
      effort: 'medium', impact: 'high', premium: false },
    { rank: 2, title: 'Lock in a Glossier or Sephora editorial deal',
      description: 'Both brands are top-2 audience affinity. Reach out via the brand-deal pipeline; estimated value $18K-32K per editorial set.',
      effort: 'low', impact: 'high', premium: false },
    { rank: 3, title: 'Test wellness adjacency in your content mix',
      description: 'Add 1-2 wellness posts per week to test signal pickup. Wellness is your top-4 interest and a high-margin product vertical.',
      effort: 'low', impact: 'medium', premium: false },
  ],

  action_plan_full: [
    { rank: 1, title: 'Pilot a private-label skincare SKU',
      description: 'Use Lumina Naturals\' 4-week MOQ to test a hydrating serum at $24-28. Estimated yr-1 GMV: $32K. Highest-impact action.',
      effort: 'medium', impact: 'high', premium: false },
    { rank: 2, title: 'Lock in a Glossier or Sephora editorial deal',
      description: 'Both are top-2 audience affinity. Outreach now; estimated value $18K-38K per editorial set across 2-3 placements.',
      effort: 'low', impact: 'high', premium: false },
    { rank: 3, title: 'Add a Wed/Sat 6-9pm posting cadence',
      description: 'Your engagement heatmap shows peak ER on Wed and Sat 6-9pm. Lock those slots for your highest-converting Reels.',
      effort: 'low', impact: 'medium', premium: true },
    { rank: 4, title: 'Test wellness adjacency in content mix',
      description: 'Add 1-2 wellness posts per week. Wellness is top-4 audience interest with strong margin potential.',
      effort: 'low', impact: 'medium', premium: false },
    { rank: 5, title: 'Launch a digital "skincare starter" guide',
      description: 'Tutorial-style content already accounts for 17% of your format mix. Bundle into a paid digital product at $24.',
      effort: 'medium', impact: 'medium', premium: true },
    { rank: 6, title: 'Open a quarterly review cadence',
      description: 'Track ER, GMV, and brand-deal pipeline progress vs your cohort each quarter. Scale plan unlocks the dashboard.',
      effort: 'low', impact: 'medium', premium: true },
  ],

  premium_preview: {
    sections: ['Product matches', 'Vendor matches', 'Brand-deal pipeline', 'Posting timing', 'Per-category breakdown', 'Full action plan'],
    bullets: [
      'Top 5 products ranked to your audience with score + GMV bands',
      'Pre-vetted vendor pipeline with commission ranges',
      'Pre-qualified brand deals with estimated value bands',
      'Monthly per-category monetization breakdown',
      'Posting timing heatmap (your highest-engagement windows)',
      '6+ priority actions tagged by effort & impact',
    ],
  },
};

export const DEMO_REPORT_PAYLOAD: ReportPayload = {
  id: 0,
  variant: 'premium',
  plan_at_generation: 'pro',
  platform: 'instagram',
  handle: 'ember_kalani',
  profile_url: 'https://instagram.com/ember_kalani',
  created_at: new Date().toISOString(),
  pdf_object_key: null,
  sections: DEMO_REPORT_SECTIONS,
};

export const DEMO_REPORT_ENTITLEMENT: Entitlement = {
  plan: 'pro',
  status: 'active',
  premium_unlocked: true,
  free_used: 0,
  free_lifetime_cap: 5,
  free_remaining: 5,
  can_generate_free: true,
  current_period_end: null,
};
